<template>
	<IsBusySectionComponent v-if="busyLoadingData || isLoading" />
	<div
		class="customer-body"
		v-else
	>
		<AccordionInfoApplication
			v-if="((isCustomerCreating && selectedStoreCode) || selectedCustomerId) && displayCreateLead"
			:store="selectedStore"
			v-model:prefilledData="prefilledData"
			:applicationSent="leadCreated"
			:createLead="createLead"
			v-model:isLoading="busyLoadingData"
			:validation="v$"
		/>

		<PanelCustomer
			v-else-if="(isCustomerCreating && selectedStoreCode) || (selectedCustomerId && customer && selectedStoreCode && displayPanelCustomer)"
			v-model:customer="modifiedCustomer"
			:fimenu="metadata"
			:isCustomerTypeDisabled="!!customer.id"
			:fallbackzipcode="customer.zip"
			:panelObject="{ title: '' }"
			:validation="v$.customer"
			:hide="hide"
			:panelView="true"
			:needsCreditSection="true"
			:viewOnly="false"
			:displayFullSSN="true"
			:isRenderedInCustomerPage="true"
			:openDeal="openDeal"
			ref="panelCustomer"
		/>

		<!-- <AccordionCustomerQuotes
				v-if="customer && customer.id && selectedStoreCode && displayPanelCustomer"
				:Customer="customer"
				:Store="metadata.store"
				:OpenDeal="openDeal"
			/> -->

		<!-- <AccordionCustomerDeals
			v-if="customer && customer.id && selectedStoreCode && displayPanelCustomer"
			:Customer="customer"
			:Store="metadata.store"
			:OpenDeal="openDeal"
		/> -->

		<div
			v-else-if="!selectedStoreCode || !displayPanelCustomer"
			style="width: 100%; text-align: center"
		>
			<h1 v-if="!selectedStoreCode">Please select a store</h1>
			<h1 v-else-if="!displayPanelCustomer">Please select a customer</h1>
		</div>
	</div>
</template>
<script lang="ts">
	export interface ICustomerDetailsProps {
		selectedStoreCode?: string;
		selectedStore?: any;
		customer?: FIMenuCustomer;
		metadata: any;
		selectedCustomerId?: string;
		customerStores?: string[];
		createCustomer: any;
		deals: any[];
		maxDealsInTab?: number;
		isLoading: boolean;
		openDeal: (() => void) | ((...args: any) => void);
		isCustomerCreating: boolean;
	}
</script>
<script setup lang="ts">
	import { computed, inject, ref, toRef, useTemplateRef } from 'vue';
	import AccordionInfoApplication from '@/components/panel/AccordionInfoApplication.vue';
	import api from '@core/services/api/_api';
	import ApplicationsPreFilledData from '@core/classes/Applications/ApplicationsPreFilledData';
	import ENUMS from '@core/classes/Enums';
	import FIMenuCustomer from '@core/classes/FIMenuCustomer';
	import IsBusySectionComponent from '@/components/IsBusySectionComponent.vue';
	import LogRocket from 'logrocket';
	import PanelCustomer from '@/components/PanelCustomer.vue';
	import { SaturnGlobalPlugin } from '@core/classes/StaticClasses';
	import settings from 'settings';
	import { useRouter } from 'vue-router';
	import { useVuelidate } from '@vuelidate/core';
	import util from '@core/services/util';
	const {
		createCustomer,
		customer = new FIMenuCustomer(),
		customerStores,
		deals,
		isCustomerCreating,
		isLoading = true,
		maxDealsInTab = 5,
		metadata,
		openDeal,
		selectedCustomerId,
		selectedStore,
		selectedStoreCode,
	} = defineProps<ICustomerDetailsProps>();

	const emits = defineEmits([
		'update:isLoading',
		'update:customer',
		'update:customerStores',
		'update:selectedCustomerId',
		'update:selectedStoreCode',
		'update:selectedStore',
	]);

	const $router = useRouter();
	const $global = inject<SaturnGlobalPlugin>('$global');

	const panelCustomerRef = useTemplateRef('panelCustomer');

	const prefilledData = ref(new ApplicationsPreFilledData());
	const leadCreated = ref(false);
	const hide = ref(['driversLicense']);
	const busyLoadingData = ref(false);
	const modifiedCustomer = toRef(() => customer);

	async function createLead() {
		try {
			emits('update:isLoading', true);
			if (displayCreateLead.value) {
				// Creates lead and cusotmer application

				const response = await api.applications
					.createApplication({
						baseUrl: window.location.host,
						isInStore: false,
						prefilledData: {
							...prefilledData.value,
							storeCode: selectedStoreCode,
						},
						createAppParams: {
							language: prefilledData.value.language,
							leadSource: 'instore',
						},
					})
					.catch((err: any) => {
						console.error(err);
						util.toastr('error', 'Error', 'Could not create customer.');
					})
					.then((response: any) => {
						if (!response) {
							return util.toastr('error', 'Error', 'Could not create customer.');
						}

						const leadId = response.data?.leadId;
						LogRocket.track(`Lead_Created`, {
							leadId: leadId,
							customerCell: prefilledData.value.phoneNumber,
							customerEmail: prefilledData.value.emailAddress,
							customerDob: prefilledData.value.dateOfBirth,
							storeName: metadata.store.storeName,
							environment: settings.environmentName,
						});
						leadCreated.value = true;
						util.toastr('success', 'Success', 'Customer created and email sent to client.');
					});
			} else if (displayPanelCustomer.value) {
				// Creates customer.
				await createCustomer();
			}
		} catch (err) {
			console.error(err);
		} finally {
			$router.push({
				name: 'customers',
				query: { ct: 'leads' },
			});

			emits('update:isLoading', false);
		}
	}

	const storeCanRetrieveApplication = computed(
		(): boolean => selectedStore?.storeSettings?.customerApplication == ENUMS.CUSTOMER_RETRIEVAL_STATUS.RetrieveApplicationEnabled,
	);
	const hasCustomer = computed((): boolean => !!customer.id);
	const hasCustomerApplication = computed((): boolean => !!customer?.customerApplication?.id);
	const hasAgreedTerms = computed((): boolean => customer.customerApplication?.hasAgreedTerms());

	const displayCreateLead = computed(
		(): boolean => !!selectedStoreCode && !!selectedStore && !hasCustomer.value && storeCanRetrieveApplication.value && !$global.isAdminView,
	);
	const displayPanelCustomer = computed((): boolean => !!selectedStoreCode && (!!selectedCustomerId || isCustomerCreating));

	const validations = computed(() => ({
		customer: modifiedCustomer.value.validation(null, FIMenuCustomer.validationTypes.CUSTOMER_CREATION),

		prefilledData: {
			...prefilledData.value.validation(),
		},
	}));

	const v$ = useVuelidate(validations, { customer: modifiedCustomer, prefilledData });
</script>
<style scoped>
	.customer-information-wrapper {
		display: flex;
		flex-direction: column;
		width: 100%;
		align-items: center;
	}
</style>
